import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart
} from "recharts";
import {
  X,
  CircleNotch,
  TrendUp,
  TrendDown,
  Calendar,
  ChartLine,
  CaretUp,
  CaretDown
} from "@phosphor-icons/react";
import { ChartWatermark } from "@/components/FarmwatchLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function PriceHistoryModal({ mart, category, categoryName, onClose }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API}/prices/irish-marts/${encodeURIComponent(mart.mart_name)}/history`,
        { params: { category, days } }
      );
      
      // Ensure the last data point (today) uses the mart's current price from the table
      // This prevents mismatches between table and chart when prices update
      const historyData = response.data;
      if (historyData.history && historyData.history.length > 0 && mart.price) {
        // Update the last point in history to match the table's current price
        historyData.history[historyData.history.length - 1].price = mart.price;
        // Also update the stats.current to match
        historyData.stats.current = mart.price;
      }
      
      setHistory(historyData);
    } catch (err) {
      setError("Failed to load price history");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [mart, category, days]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatPrice = (price) => {
    if (history?.unit === "per head") {
      return `€${price?.toFixed(0)}`;
    }
    return `€${price?.toFixed(2)}`;
  };

  const formatTooltipValue = (value, name) => {
    if (value === null || value === undefined) return ["-", name];
    const formatted = history?.unit === "per head" ? `€${value.toFixed(0)}` : `€${value.toFixed(2)}`;
    return [formatted, name];
  };

  const getChangeColor = (change) => {
    if (change > 0) return "text-[#059669]";
    if (change < 0) return "text-[#DC2626]";
    return "text-[#6B6661]";
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#111827] border border-[#1F2937] w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-[#111827] border-b border-[#1F2937] p-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-serif text-xl text-white flex items-center gap-2">
              <ChartLine size={24} className="text-[#0F766E]" />
              Price History
            </h2>
            <p className="text-sm text-[#8B8580] mt-1">
              {mart.mart_name} • {mart.county} • {categoryName}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-[#8B8580] hover:text-white">
            <X size={24} />
          </button>
        </div>

        {/* Time Range Selector */}
        <div className="p-4 border-b border-[#1F2937] flex items-center gap-2">
          <Calendar size={18} className="text-[#8B8580]" />
          <span className="text-sm text-[#8B8580] mr-2">Period:</span>
          {[30, 60, 90, 180, 365].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`period-${d}`}
              className={`px-3 py-1 text-sm font-mono ${
                days === d
                  ? "bg-[#0F766E] text-white"
                  : "bg-[#1F2937] text-[#8B8580] hover:text-white"
              }`}
            >
              {d}D
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <CircleNotch size={32} className="animate-spin text-[#0F766E] mx-auto mb-2" />
            <span className="text-[#6B6661] text-sm">Loading price history...</span>
          </div>
        ) : error ? (
          <div className="p-12 text-center text-[#DC2626]">{error}</div>
        ) : history ? (
          <>
            {/* Stats */}
            <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-4 border-b border-[#1F2937]">
              <div className="bg-[#1F2937] p-3">
                <p className="text-xs text-[#6B6661] uppercase">Current</p>
                <p className="font-mono text-xl text-white">{formatPrice(mart.price)}</p>
              </div>
              <div className="bg-[#1F2937] p-3">
                <p className="text-xs text-[#6B6661] uppercase">Average</p>
                <p className="font-mono text-xl text-[#60A5FA]">{formatPrice(history.stats.average)}</p>
              </div>
              <div className="bg-[#1F2937] p-3">
                <p className="text-xs text-[#6B6661] uppercase">High</p>
                <p className="font-mono text-xl text-[#34D399]">{formatPrice(history.stats.max)}</p>
              </div>
              <div className="bg-[#1F2937] p-3">
                <p className="text-xs text-[#6B6661] uppercase">Low</p>
                <p className="font-mono text-xl text-[#F87171]">{formatPrice(history.stats.min)}</p>
              </div>
              <div className="bg-[#1F2937] p-3">
                <p className="text-xs text-[#6B6661] uppercase">30D Change</p>
                <div className="flex items-center gap-1">
                  {history.stats.change_30d >= 0 ? (
                    <CaretUp size={16} className="text-[#34D399]" weight="fill" />
                  ) : (
                    <CaretDown size={16} className="text-[#F87171]" weight="fill" />
                  )}
                  <p className={`font-mono text-xl ${getChangeColor(history.stats.change_30d)}`}>
                    {history.stats.change_30d >= 0 ? "+" : ""}{history.stats.change_30d_percent}%
                  </p>
                </div>
              </div>
            </div>

            {/* Chart */}
            <div className="p-4 relative" data-testid="price-chart">
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={history.history}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#6B7280"
                    tick={{ fill: '#6B7280', fontSize: 11 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                    }}
                    interval={Math.floor(history.history.length / 8)}
                  />
                  <YAxis 
                    stroke="#6B7280"
                    tick={{ fill: '#6B7280', fontSize: 11 }}
                    domain={['dataMin - 0.1', 'dataMax + 0.1']}
                    tickFormatter={(value) => history.unit === "per head" ? `€${value.toFixed(0)}` : `€${value.toFixed(2)}`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1F2937', 
                      border: '1px solid #374151',
                      borderRadius: 0
                    }}
                    labelStyle={{ color: '#9CA3AF' }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      const formatValue = (val) => history?.unit === "per head" ? `€${val?.toFixed(0)}` : `€${val?.toFixed(2)}`;
                      return (
                        <div style={{ backgroundColor: '#1F2937', border: '1px solid #374151', padding: '10px' }}>
                          <p style={{ color: '#9CA3AF', marginBottom: '8px' }}>
                            {new Date(label).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                          {payload.map((entry) => {
                            // Use white for Daily Price, keep original colors for others
                            const color = entry.name === "Daily Price" ? "#FFFFFF" : entry.color;
                            return (
                              <p key={entry.dataKey || entry.name} style={{ color, margin: '4px 0' }}>
                                {entry.name}: {formatValue(entry.value)}
                              </p>
                            );
                          })}
                        </div>
                      );
                    }}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="price" 
                    fill="#0F766E" 
                    fillOpacity={0.1}
                    stroke="#0F766E"
                    strokeWidth={2}
                    name="Daily Price"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="ma_7" 
                    stroke="#10B981" 
                    strokeWidth={1.5}
                    dot={false}
                    name="7-Day MA"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="ma_30" 
                    stroke="#064E3B" 
                    strokeWidth={1.5}
                    strokeDasharray="5 5"
                    dot={false}
                    name="30-Day MA"
                  />
                </ComposedChart>
              </ResponsiveContainer>
              <ChartWatermark position="bottom-right" size="small" />
            </div>

            {/* Legend Explanation */}
            <div className="px-4 pb-4">
              <div className="bg-[#1F2937] p-3 text-xs text-[#8B8580]">
                <strong className="text-white">Reading the chart:</strong> The teal area shows daily prices. 
                The emerald line (7-Day MA) smooths out daily fluctuations. 
                The dark green dashed line (30-Day MA) shows longer-term trends.
                When the 7-day crosses above the 30-day, prices may be rising.
              </div>
            </div>
          </>
        ) : null}

        {/* Footer */}
        <div className="p-4 border-t border-[#1F2937] flex justify-between items-center">
          <p className="text-xs text-[#6B6661]">
            Market day: {history?.market_day || mart.market_day}
          </p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-[#0F766E] text-white font-sans hover:bg-[#14B8A6]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
